import { forwardRef, useState, useCallback, type ChangeEvent, useId } from 'react';

import { cn } from '../../utils';
import { FlagIcon } from '../FlagIcon';

export interface PhoneInputProps {
  /** Current value (only digits) */
  value?: string;
  /** Callback when value changes */
  onChange?: (value: string) => void;
  /** Callback when the digits input loses focus */
  onBlur?: () => void;
  /** Country code shown inside the field next to the flag */
  countryCode?: string;
  /** Notched label floating over the border */
  label?: string;
  /** Error message (renders below, and recolors the border) */
  error?: string;
  /** Hint text (renders below when there is no error) */
  hint?: string;
  /** Placeholder text shown inside the digits input */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Height variant */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className (applied to the outer wrapper) */
  className?: string;
  /** ID for the input */
  id?: string;
  /** Name for the input */
  name?: string;
}

const containerSize = {
  sm: 'h-10',
  md: 'h-12',
  lg: 'h-14',
};

const textSize = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
};

function formatPhoneNumber(digits: string): string {
  const cleaned = digits.replace(/\D/g, '').slice(0, 10);
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 2) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
}

function extractDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 10);
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = '',
      onChange,
      onBlur,
      countryCode = '+52',
      label,
      error,
      hint,
      placeholder = '',
      disabled = false,
      size = 'md',
      className,
      id,
      name,
    },
    ref
  ) => {
    const reactId = useId();
    const inputId = id || `phone-${reactId}`;
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = useState(false);
    const [displayValue, setDisplayValue] = useState(() => formatPhoneNumber(value));

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const digits = extractDigits(e.target.value);
        setDisplayValue(formatPhoneNumber(digits));
        onChange?.(digits);
      },
      [onChange]
    );

    const formattedExternalValue = formatPhoneNumber(value);
    if (formattedExternalValue !== displayValue && value !== extractDigits(displayValue)) {
      setDisplayValue(formattedExternalValue);
    }

    const borderColor = hasError
      ? 'border-tkf-line-error'
      : isFocused
        ? 'border-primary-500'
        : 'border-tkf-line-medium';

    const labelColor = hasError
      ? 'text-tkf-fg-error'
      : isFocused
        ? 'text-primary-500'
        : 'text-tkf-fg-muted';

    return (
      <div className={cn('w-full', className)}>
        <div className="relative">
          {label && (
            <label
              htmlFor={inputId}
              style={{
                background: disabled
                  ? 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface-muted) 45%)'
                  : 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface) 45%)',
              }}
              className={cn(
                'pointer-events-none absolute left-3 top-0 -translate-y-1/2',
                'px-1 text-sm font-medium transition-colors duration-200',
                labelColor,
                disabled && 'text-tkf-fg-disabled'
              )}
            >
              {label}
            </label>
          )}

          <div
            className={cn(
              'flex w-full items-center rounded-lg border bg-tkf-surface px-3 transition-colors duration-200',
              containerSize[size],
              borderColor,
              disabled && 'cursor-not-allowed bg-tkf-surface-muted text-tkf-fg-disabled'
            )}
          >
            <div
              className={cn(
                'flex items-center gap-2 pr-3 font-medium text-tkf-fg-muted',
                textSize[size]
              )}
            >
              <FlagIcon code="MX" size="md" />
              <span>{countryCode}</span>
            </div>

            <input
              ref={ref}
              id={inputId}
              name={name}
              type="tel"
              inputMode="numeric"
              value={displayValue}
              onChange={handleChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                onBlur?.();
              }}
              placeholder={placeholder}
              disabled={disabled}
              autoComplete="tel-national"
              aria-invalid={hasError}
              aria-describedby={
                hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
              }
              className={cn(
                'flex-1 bg-transparent font-medium text-tkf-fg outline-none',
                'placeholder:text-tkf-fg-subtle disabled:cursor-not-allowed',
                textSize[size]
              )}
            />
          </div>
        </div>

        {error && (
          <p id={`${inputId}-error`} className="mt-1 text-sm text-tkf-fg-error" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={`${inputId}-hint`} className="mt-1 text-sm text-tkf-fg-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
